import type { CmmdRuntimeInventoryRuntime, CmmdRuntimeThreadReplica } from "@cmmd-forge/contracts";

/**
 * Local-only example transcript for verifying the CMMD Forge iframe thread
 * viewer without a real Sprites runtime or Start-task path.
 *
 * Opened through the product host route:
 * `/forge?runtimeId=rt-demo&threadId=th-demo`
 *
 * Inventory + replica must both name a live Environment so the thread viewer
 * stays on the ordinary open-thread path (composer usable, no offline saved-
 * history chrome). Real offline / deleted-runtime threads keep their warnings.
 */
const EMBED_THREAD_VIEWER_EXAMPLE_RUNTIME_ID = "rt-demo";
const EMBED_THREAD_VIEWER_EXAMPLE_THREAD_ID = "th-demo";

const EXAMPLE_PROJECT_ID = "example-project";
const EXAMPLE_CONVERSATION_ID = "example-conversation";
const EXAMPLE_ACTIVITY_AT = "2026-09-26T20:00:00.000Z";

export function isEmbedThreadViewerExample(runtimeId: string, threadId: string): boolean {
  return (
    runtimeId === EMBED_THREAD_VIEWER_EXAMPLE_RUNTIME_ID &&
    threadId === EMBED_THREAD_VIEWER_EXAMPLE_THREAD_ID
  );
}

/** Synthetic inventory row so `findRemoteRuntimeThreadRouteTarget` is not history-only. */
function embedThreadViewerExampleInventoryRuntime(): CmmdRuntimeInventoryRuntime {
  return {
    runtime: {
      id: EMBED_THREAD_VIEWER_EXAMPLE_RUNTIME_ID,
      source: "forge-cloud",
      runtimeKind: "cloud-vm",
      displayName: "Example Forge Server",
      status: "online",
      endpointUrl: null,
      hostId: null,
      deviceId: null,
      workspaceRoot: null,
      githubRepoFullName: null,
      lastSeenAt: EXAMPLE_ACTIVITY_AT,
      reachability: {
        status: "online",
        lastReachableAt: EXAMPLE_ACTIVITY_AT,
        source: "cloud-runtime",
      },
      metadata: null,
    },
    inventoryStatus: "synced",
    inventoryPublishedAt: EXAMPLE_ACTIVITY_AT,
    projectCount: 1,
    threadCount: 1,
    projects: [
      {
        projectId: EXAMPLE_PROJECT_ID,
        title: "Example project",
        workspaceRoot: null,
        githubRepoFullName: null,
        branch: null,
        status: "active",
        threadCount: 1,
        lastActivityAt: EXAMPLE_ACTIVITY_AT,
        metadata: null,
        publishedAt: EXAMPLE_ACTIVITY_AT,
        updatedAt: EXAMPLE_ACTIVITY_AT,
        threads: [
          {
            threadId: EMBED_THREAD_VIEWER_EXAMPLE_THREAD_ID,
            title: "Example thread (viewer check)",
            status: "active",
            model: null,
            provider: null,
            providerAccountId: null,
            branch: null,
            lastMessagePreview: null,
            lastActivityAt: EXAMPLE_ACTIVITY_AT,
            kind: "master",
            parentThreadId: null,
            workerRole: null,
            anchorKind: "scratch",
            anchorCmmdId: null,
            archive: null,
            disposition: {
              manualDisposition: null,
              automaticDisposition: null,
              effectiveDisposition: null,
              revision: null,
              source: null,
            },
            metadata: null,
            publishedAt: EXAMPLE_ACTIVITY_AT,
            updatedAt: EXAMPLE_ACTIVITY_AT,
          },
        ],
      },
    ],
  };
}

export function embedThreadViewerExampleReplica(): CmmdRuntimeThreadReplica {
  return {
    conversationId: EXAMPLE_CONVERSATION_ID,
    runtime: {
      id: EMBED_THREAD_VIEWER_EXAMPLE_RUNTIME_ID,
      source: "forge-cloud",
      runtimeKind: "cloud-vm",
      displayName: "Example Forge Server",
      status: "online",
      endpointUrl: null,
      deviceId: null,
      workspaceRoot: null,
      githubRepoFullName: null,
      lastSeenAt: EXAMPLE_ACTIVITY_AT,
      metadata: null,
    },
    project: {
      projectId: EXAMPLE_PROJECT_ID,
      title: "Example project",
      githubRepoFullName: null,
      branch: null,
    },
    thread: {
      threadId: EMBED_THREAD_VIEWER_EXAMPLE_THREAD_ID,
      title: "Example thread (viewer check)",
      status: "active",
      model: null,
      provider: null,
      providerAccountId: null,
      branch: null,
      lastActivityAt: EXAMPLE_ACTIVITY_AT,
      metadata: null,
    },
    reachability: "online",
    execution: { available: true, reason: null },
    replication: null,
    events: [
      {
        sequence: 1,
        eventId: "example-user-1",
        kind: "user_message",
        turnId: null,
        text: "This is an example chat thread for verifying the Forge embed viewer. It is not a live Work Session.",
        status: null,
        occurredAt: "2026-09-26T19:58:00.000Z",
      },
      {
        sequence: 2,
        eventId: "example-assistant-1",
        kind: "assistant_message",
        turnId: null,
        text: "Understood. You should see this reply in the iframe under CMMD /forge, with the composer still available below.",
        status: null,
        occurredAt: "2026-09-26T19:59:00.000Z",
      },
      {
        sequence: 3,
        eventId: "example-user-2",
        kind: "user_message",
        turnId: null,
        text: "Thanks. If these three turns render, the thread wire is working.",
        status: null,
        occurredAt: EXAMPLE_ACTIVITY_AT,
      },
    ],
  };
}

/** Merge the example runtime into an inventory snapshot when it is not already present. */
export function withEmbedThreadViewerExampleInventory(
  snapshot: { readonly runtimes: readonly CmmdRuntimeInventoryRuntime[] } & Record<string, unknown>,
): typeof snapshot & { readonly runtimes: CmmdRuntimeInventoryRuntime[] } {
  const alreadyPresent = snapshot.runtimes.some(
    (runtime) => runtime.runtime.id === EMBED_THREAD_VIEWER_EXAMPLE_RUNTIME_ID,
  );
  if (alreadyPresent) {
    return { ...snapshot, runtimes: [...snapshot.runtimes] };
  }
  return {
    ...snapshot,
    runtimes: [...snapshot.runtimes, embedThreadViewerExampleInventoryRuntime()],
  };
}
